// src/pages/planejamento/NovaVisitaPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { collection, doc, getDoc, getDocs, limit, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";

import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePlanejamentoAtendimentos } from "../../hooks/usePlanejamentoAtendimentos";
import { db } from "../../config/firebase";
import { NIVEIS } from "../../config/niveis";
import { nivelUsuario } from "../../utils/permissoes";
import TransferList from "../../components/TransferList/TransferList";
import { calcularCamadaExecutar, formatarCamadaLabel } from "../../utils/preventivaCamadas";

import "./NovaVisitaPage.css";

const GLOBAL_CLIENTE_ID = "__GLOBAL__";

function normStr(v) {
  return String(v || "").trim().toLowerCase();
}

function isContratoAvulso(tipo) {
  return normStr(tipo).includes("avulso");
}

function isContratoQuinzenal(tipo) {
  return normStr(tipo).includes("quinzen");
}

function referenciaFromCompetencia(competencia) {
  const c = String(competencia || "").trim();
  const m = c.match(/^(\d{4})-(\d{2})(?:-(Q1|Q2))?$/);
  if (!m) return new Date();
  const ano = Number(m[1]);
  const mes = Number(m[2]) - 1;
  const dia = m[3] === "Q2" ? 15 : 1;
  return new Date(ano, mes, dia, 12, 0, 0, 0);
}

function normalizarTipoItem(item) {
  const tipoRaw = String(item?.tipo || "").toUpperCase();
  if (tipoRaw === "OS" || item?.osId) return "OS";
  if (tipoRaw === "PREVENTIVA" || item?.preventivaId) return "PREVENTIVA";
  if (tipoRaw === "INSTALACAO" || item?.checklistInstalacaoId || item?.propostaInstalacaoId) return "INSTALACAO";
  return "EQUIPAMENTO";
}

function obterTipoVisita(itens = []) {
  const tipos = new Set((itens || []).map((i) => normalizarTipoItem(i)).filter(Boolean));
  return tipos.size === 1 ? Array.from(tipos)[0] : null;
}

export default function NovaVisitaPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario } = useAuth();
  const isMobile = useIsMobile(900);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Permite pré-preencher a tela quando o usuário vem do Calendário/Atendimento
  const urlClienteId = searchParams.get("clienteId") || "";
  const urlContratoTipo = searchParams.get("contratoTipo") || "";
  const urlAcademiaId = searchParams.get("academiaId") || "";
  const urlCompetencia = searchParams.get("competencia") || "";
  const urlCompetenciaMes =
    searchParams.get("competenciaMes") || (urlCompetencia ? String(urlCompetencia).slice(0, 7) : "");
  const urlQuinzena =
    searchParams.get("quinzena")
    || (String(urlCompetencia).includes("-Q2") ? "Q2" : (String(urlCompetencia).includes("-Q1") ? "Q1" : ""));
  const urlReturnTo = searchParams.get("returnTo") || "";

  const {
    scope,
    criarAtendimento,
    listarAtendimentosPorClienteCompetencia,
    criarSessao,
    adicionarItensSessao,
  } = usePlanejamentoAtendimentos();

  const [clientes, setClientes] = useState([]);
  const [academias, setAcademias] = useState([]);

  const [clienteBusca, setClienteBusca] = useState("");
  const [clienteId, setClienteId] = useState(urlClienteId);
  const [contratoTipo, setContratoTipo] = useState(urlContratoTipo || "TODOS");
  const [academiaId, setAcademiaId] = useState(urlAcademiaId);
  const defaultsCompetencia = (() => {
    const d = new Date();
    let y = d.getFullYear();
    let m = d.getMonth() + 1;
    let q = "Q2";

    // Regra: a primeira preventiva é na PRÓXIMA quinzena
    // - se hoje é dia 1..14 -> próxima é Q2 (dia 15) do mês atual
    // - se hoje é dia 15..31 -> próxima é Q1 (dia 1) do próximo mês
    if (d.getDate() > 14) {
      q = "Q1";
      m += 1;
      if (m === 13) {
        m = 1;
        y += 1;
      }
    }

    return {
      competenciaMes: `${y}-${String(m).padStart(2, "0")}`,
      quinzena: q,
    };
  })();

  const [competenciaMes, setCompetenciaMes] = useState(urlCompetenciaMes || "");
  const [quinzena, setQuinzena] = useState(urlQuinzena || "");

  const [itensVisita, setItensVisita] = useState([]);

  // Mudou a competência/quinzena/contrato -> reinicia seleção para evitar misturar atendimentos
  useEffect(() => {
    setItensVisita([]);
  }, [competenciaMes, quinzena, contratoTipo]);


  const [backlogPreventivas, setBacklogPreventivas] = useState([]);
  const [backlogOs, setBacklogOs] = useState([]);
  const [backlogInstalacoes, setBacklogInstalacoes] = useState([]);
  const [loadingBacklog, setLoadingBacklog] = useState(false);

  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const competencia = useMemo(() => {
    if (String(contratoTipo || "").toUpperCase() === "TODOS") return "";
    if (!competenciaMes) return "";
    if (isContratoQuinzenal(contratoTipo)) {
      const q = quinzena || (new Date().getDate() >= 15 ? "Q2" : "Q1");
      return `${competenciaMes}-${q}`;
    }
    return competenciaMes;
  }, [competenciaMes, quinzena, contratoTipo]);

  const isGlobalCliente = clienteId === GLOBAL_CLIENTE_ID;
  const empresaIdForOps = !isGlobalCliente && (scope?.empresaId || clienteId);

  useEffect(() => {
    if (!scope?.grupoId) return;

    const carregarClientes = async () => {
      try {
        const q = query(collection(db, "empresas"), where("grupoId", "==", scope.grupoId));
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));

        const nivel = nivelUsuario(usuario);
        const podeVerTodas =
          usuario?.acessoGlobal === true ||
          usuario?.globalAccess === true ||
          usuario?.tipoAcesso === "GLOBAL" ||
          nivel >= NIVEIS.ADMIN;

        const permitidasRaw = Array.isArray(usuario?.empresasPermitidas)
          ? usuario.empresasPermitidas
          : [];
        const permitidasIds = permitidasRaw.map((e) => e?.id).filter(Boolean);
        const permitidasNomes = permitidasRaw
          .map((e) => String(e?.nome || "").trim())
          .filter(Boolean);

        let listaFiltrada = lista;
        if (!podeVerTodas) {
          if (permitidasIds.length) {
            listaFiltrada = lista.filter((e) => permitidasIds.includes(e.id));
          } else if (permitidasNomes.length) {
            listaFiltrada = lista.filter((e) => permitidasNomes.includes(String(e.nome || "").trim()));
          } else if (usuario?.empresaId) {
            listaFiltrada = lista.filter((e) => String(e.id) === String(usuario.empresaId));
          } else if (usuario?.empresa || usuario?.empresaNome) {
            const n = String(usuario.empresa || usuario.empresaNome || "").trim();
            if (n) listaFiltrada = lista.filter((e) => String(e.nome || "").trim() === n);
          }
        }

        setClientes(listaFiltrada);
      } catch (e) {
        console.error("Erro ao carregar clientes:", e);
      }
    };

    carregarClientes();
  }, [scope?.grupoId, usuario]);

  // Carrega academias (setores) do cliente
  useEffect(() => {
    if (!scope?.grupoId || !clienteId || isGlobalCliente) {
      setAcademias([]);
      setAcademiaId("");
      return;
    }

    (async () => {
      try {
        const q = query(
          collection(db, "setores"),
          where("grupoId", "==", scope.grupoId),
          where("empresaId", "==", clienteId)
        );
        const snap = await getDocs(q);
        const lista = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => String(a.nome || "").localeCompare(String(b.nome || ""), "pt-BR", { sensitivity: "base" }));
        setAcademias(lista);
      } catch (e) {
        console.error("Erro ao carregar academias:", e);
        setAcademias([]);
      }
    })();
  }, [scope?.grupoId, clienteId, isGlobalCliente]);

  const clientesFiltrados = useMemo(() => {
    const term = normStr(clienteBusca);
    if (!term) return clientes;
    return clientes.filter((c) => normStr(c.nome || c.razaoSocial || "").includes(term));
  }, [clientes, clienteBusca]);

  // Ao selecionar cliente, limpa academia e mantem o contrato atual
  useEffect(() => {
    if (!clienteId) return;
    setAcademiaId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  // Carregar backlog (preventivas + OS) do cliente selecionado
  useEffect(() => {
    const carregarBacklog = async () => {
      if (!scope?.grupoId) {
        setBacklogPreventivas([]);
        setBacklogOs([]);
        setBacklogInstalacoes([]);
        return;
      }

      setLoadingBacklog(true);
      try {
        const aplicaFiltroCliente = !!clienteId && !isGlobalCliente;
        const aplicaFiltroContrato = !!contratoTipo && String(contratoTipo).toUpperCase() !== "TODOS";

        if (!aplicaFiltroContrato || !isContratoAvulso(contratoTipo)) {
          const qPrev = aplicaFiltroCliente
            ? query(
                collection(db, "preventivas"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", clienteId),
                limit(700)
              )
            : query(
                collection(db, "preventivas"),
                where("grupoId", "==", scope.grupoId),
                limit(700)
              );

          const qMaquinas = aplicaFiltroCliente
            ? query(
                collection(db, "maquinas"),
                where("grupoId", "==", scope.grupoId),
                where("empresaId", "==", clienteId)
              )
            : query(
                collection(db, "maquinas"),
                where("grupoId", "==", scope.grupoId)
              );

          const [snapPrev, snapMaquinas] = await Promise.all([
            getDocs(qPrev),
            getDocs(qMaquinas),
          ]);

          let listaPrev = snapPrev.docs.map((d) => ({ id: d.id, ...d.data() }));
          listaPrev = listaPrev.filter((p) => p?.ativo !== false);

          const maquinasAtivasIds = new Set(snapMaquinas.docs.map((d) => String(d.id)));
          listaPrev = listaPrev.filter((p) => {
            const maquinaId = p?.maquinaId ? String(p.maquinaId) : "";
            if (!maquinaId) return true;
            return maquinasAtivasIds.has(maquinaId);
          });

          const hojeRef = new Date();
          hojeRef.setHours(0, 0, 0, 0);
          listaPrev = listaPrev.filter((p) => {
            const dt = p?.proximaData?.toDate
              ? p.proximaData.toDate()
              : (p.proximaData ? new Date(p.proximaData) : null);
            if (!dt || Number.isNaN(dt.getTime())) return true;
            dt.setHours(0, 0, 0, 0);
            return dt <= hojeRef;
          });
          
          listaPrev.sort((a, b) => {
            const da = a.proximaData?.toDate ? a.proximaData.toDate() : (a.proximaData ? new Date(a.proximaData) : null);
            const dbv = b.proximaData?.toDate ? b.proximaData.toDate() : (b.proximaData ? new Date(b.proximaData) : null);
            if (!da && !dbv) return 0;
            if (!da) return 1;
            if (!dbv) return -1;
            return da.getTime() - dbv.getTime();
          });

          // Remove preventivas ja agendadas (existem em itens de sessao)
          try {
            let qItensPrev = query(
              collection(db, "atendimentos_sessao_itens"),
              where("grupoId", "==", scope.grupoId),
              where("tipo", "==", "PREVENTIVA")
            );

            if (aplicaFiltroCliente) {
              qItensPrev = query(
                collection(db, "atendimentos_sessao_itens"),
                where("grupoId", "==", scope.grupoId),
                where("tipo", "==", "PREVENTIVA"),
                where("clienteId", "==", clienteId)
              );
            }

            const snapItensPrev = await getDocs(qItensPrev);

            // Se houver competência selecionada, filtra itens já agendados naquela competência.
            // Sem competência, remove qualquer preventiva já agendada para evitar duplicidade.
            if (competencia) {
              const itensPrev = snapItensPrev.docs.map((d) => ({ id: d.id, ...d.data() }));

              // Itens antigos podem não ter "competencia" gravada no item:
              // busca a competência no documento de atendimento correspondente.
              const atendimentoIds = Array.from(
                new Set(itensPrev.map((i) => i.atendimentoId).filter(Boolean).map((id) => String(id)))
              );

              const competenciaByAtendimento = new Map();
              await Promise.all(
                atendimentoIds.map(async (atId) => {
                  try {
                    const snapAt = await getDoc(doc(db, "atendimentos_preventiva", atId));
                    if (snapAt.exists()) {
                      const c = snapAt.data()?.competencia;
                      if (c) competenciaByAtendimento.set(String(atId), String(c));
                    }
                  } catch (err) {
                    // silencioso
                  }
                })
              );

              const slotKeysAgendados = new Set();
              itensPrev.forEach((i) => {
                const comp = i.competencia || competenciaByAtendimento.get(String(i.atendimentoId)) || null;
                if (!comp) return;
                const slotKey = i.slotKey || (i.preventivaId ? `${i.preventivaId}|${comp}` : null);
                if (slotKey && String(comp) === String(competencia)) {
                  slotKeysAgendados.add(String(slotKey));
                }
              });

              listaPrev = listaPrev.filter((prev) => !slotKeysAgendados.has(`${prev.id}|${competencia}`));
            } else {
              const prevAgendadas = new Set(
                snapItensPrev.docs
                  .map((d) => d.data()?.preventivaId)
                  .filter(Boolean)
                  .map((id) => String(id))
              );
              listaPrev = listaPrev.filter((prev) => !prevAgendadas.has(String(prev.id)));
            }
          } catch (e) {
            console.warn("Erro ao filtrar preventivas agendadas:", e);
          }

          setBacklogPreventivas(listaPrev);
        } else {
          setBacklogPreventivas([]);
        }

        let qOs = query(
          collection(db, "ordensServico"),
          where("grupoId", "==", scope.grupoId),
          limit(500)
        );

        if (aplicaFiltroCliente) {
          qOs = query(
            collection(db, "ordensServico"),
            where("grupoId", "==", scope.grupoId),
            where("empresaId", "==", clienteId),
            limit(500)
          );
        }

        const snapOs = await getDocs(qOs);

        let listaOs = snapOs.docs.map((d) => ({ id: d.id, ...d.data() }));
        listaOs = listaOs.filter((os) => {
          const st = normStr(os?.statusNome || os?.status || "");
          if (st.includes("final")) return false;
          if (st.includes("cancel")) return false;
          return true;
        });

        const critRank = (c) => {
          const v = normStr(c);
          if (v.includes("alta")) return 3;
          if (v.includes("media") || v.includes("média")) return 2;
          if (v.includes("baixa")) return 1;
          return 0;
        };

        listaOs.sort((a, b) => {
          const ra = critRank(a.criticidade);
          const rb = critRank(b.criticidade);
          if (rb !== ra) return rb - ra;
          const da = a.createdAt?.toDate ? a.createdAt.toDate() : (a.createdAt ? new Date(a.createdAt) : null);
          const dbv = b.createdAt?.toDate ? b.createdAt.toDate() : (b.createdAt ? new Date(b.createdAt) : null);
          if (!da && !dbv) return 0;
          if (!da) return 1;
          if (!dbv) return -1;
          return da.getTime() - dbv.getTime();
        });

        // Remove OS ja agendadas (existem em itens de sessao)
        try {
          let qItens = query(
            collection(db, "atendimentos_sessao_itens"),
            where("grupoId", "==", scope.grupoId),
            where("tipo", "==", "OS")
          );

          if (aplicaFiltroCliente) {
            qItens = query(
              collection(db, "atendimentos_sessao_itens"),
              where("grupoId", "==", scope.grupoId),
              where("tipo", "==", "OS"),
              where("clienteId", "==", clienteId)
            );
          }

          const snapItens = await getDocs(qItens);
          const osAgendadas = new Set(
            snapItens.docs.map((d) => d.data()?.osId).filter(Boolean).map((id) => String(id))
          );
          listaOs = listaOs.filter((os) => !osAgendadas.has(String(os.id)));
        } catch (e) {
          console.warn("Erro ao filtrar OS agendadas:", e);
        }

        setBacklogOs(listaOs);

        const qInst = aplicaFiltroCliente
          ? query(
              collection(db, "financeiro_propostas_instalacoes"),
              where("grupoId", "==", scope.grupoId),
              where("clienteId", "==", clienteId),
              where("status", "==", "APROVADA"),
              limit(500)
            )
          : query(
              collection(db, "financeiro_propostas_instalacoes"),
              where("grupoId", "==", scope.grupoId),
              where("status", "==", "APROVADA"),
              limit(500)
            );

        const snapInst = await getDocs(qInst);
        let listaInst = snapInst.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((p) => !p?.sessaoId);

        try {
          let qItensInst = query(
            collection(db, "atendimentos_sessao_itens"),
            where("grupoId", "==", scope.grupoId),
            where("tipo", "==", "INSTALACAO")
          );

          if (aplicaFiltroCliente) {
            qItensInst = query(
              collection(db, "atendimentos_sessao_itens"),
              where("grupoId", "==", scope.grupoId),
              where("tipo", "==", "INSTALACAO"),
              where("clienteId", "==", clienteId)
            );
          }

          const snapItensInst = await getDocs(qItensInst);
          const instalacoesAgendadas = new Set(
            snapItensInst.docs
              .map((d) => d.data()?.propostaInstalacaoId || d.data()?.equipamentoId)
              .filter(Boolean)
              .map((id) => String(id))
          );

          listaInst = listaInst.filter((p) => !instalacoesAgendadas.has(String(p.id)));
        } catch (e) {
          console.warn("Erro ao filtrar instalações agendadas:", e);
        }

        setBacklogInstalacoes(listaInst);
      } catch (e) {
        console.error("Erro ao carregar backlog:", e);
        setBacklogPreventivas([]);
        setBacklogOs([]);
        setBacklogInstalacoes([]);
      } finally {
        setLoadingBacklog(false);
      }
    };

    carregarBacklog();
  }, [scope?.grupoId, clienteId, contratoTipo, isGlobalCliente, competencia, competenciaMes, quinzena]);

  const backlogItens = useMemo(() => {
    const prevBase = academiaId && !isGlobalCliente
      ? backlogPreventivas.filter((p) => String(p.setorId || "") === String(academiaId))
      : backlogPreventivas;
    const osBase = academiaId && !isGlobalCliente
      ? backlogOs.filter((o) => String(o.setorId || "") === String(academiaId))
      : backlogOs;
    const instalacoesBase = academiaId && !isGlobalCliente
      ? backlogInstalacoes.filter((m) => String(m.setorId || "") === String(academiaId))
      : backlogInstalacoes;

    const prev = prevBase.map((p) => {
      const dt = p.proximaData?.toDate ? p.proximaData.toDate() : (p.proximaData ? new Date(p.proximaData) : null);
      const dataLabel = dt && !Number.isNaN(dt.getTime()) ? dt.toLocaleDateString("pt-BR") : "sem data";
      const codigo = p.codigo || p.modeloPreventivaCodigo || "";
      const nomePrev = p.nome || "Preventiva";
      const label = codigo ? `${codigo} - ${nomePrev}` : nomePrev;
      const competenciaItem = competencia || "SEM_COMPETENCIA";
      const refCiclo = competencia ? referenciaFromCompetencia(competencia) : new Date();
      const camadaPrevista = calcularCamadaExecutar(p, refCiclo);
      const camadaLabelPrev = camadaPrevista ? formatarCamadaLabel(camadaPrevista) : "";
      const subLabel = [p.maquinaNome || "", `Próx: ${dataLabel}`, camadaLabelPrev ? `Prevista: ${camadaLabelPrev}` : ""].filter(Boolean).join(" • " );

      const clienteNome = p.empresaNome || "Sem cliente";
      const academiaNome = p.setorNome || "Sem academia";
      return {
        id: `PREVENTIVA:${p.id}:${competenciaItem}`,
        tipo: "PREVENTIVA",
        preventivaId: p.id,
        clienteId: p.empresaId || null,
        slotKey: competencia ? `${p.id}|${competencia}` : null,
        camadaPrevista,
        competencia: competencia || null,
        competenciaMes: competenciaMes || null,
        quinzena: (isContratoQuinzenal(contratoTipo) && quinzena) ? quinzena : null,
        setorId: p.setorId || null,
        setorNome: p.setorNome || "",
        clienteNome,
        academiaNome,
        label,
        subLabel,
      };
    });

    const os = osBase.map((o) => {
      const label = o.codigo
        ? `#${o.codigo} - ${o.titulo || o.defeitoNome || ""}`
        : (o.titulo || o.defeitoNome || o.descricao || o.id);
      const subLabel = [o.setorNome || "", o.statusNome || ""].filter(Boolean).join(" • ");
      const clienteNome = o.empresaNome || "Sem cliente";
      const academiaNome = o.setorNome || "Sem academia";
      return {
        id: `OS:${o.id}`,
        tipo: "OS",
        osId: o.id,
        clienteId: o.empresaId || null,
        competencia: competencia || null,
        competenciaMes: competenciaMes || null,
        quinzena: (isContratoQuinzenal(contratoTipo) && quinzena) ? quinzena : null,
        setorId: o.setorId || null,
        setorNome: o.setorNome || "",
        clienteNome,
        academiaNome,
        label,
        subLabel,
      };
    });

    const instalacoes = instalacoesBase.map((p) => {
      const label = p.academiaNome
        ? `Instalação • ${p.academiaNome}`
        : `Instalação ${String(p.id || "").slice(0, 6)}`;
      const subLabel = [
        p.clienteNome || "",
        p.previsaoDias ? `${p.previsaoDias} dia(s)` : "",
        p.equipamentosTotal != null ? `${p.equipamentosTotal} equip.` : "",
      ].filter(Boolean).join(" • ");
      const clienteNome = p.clienteNome || "Sem cliente";
      const academiaNome = p.academiaNome || "Sem academia";
      return {
        id: `INSTALACAO:${p.id}`,
        tipo: "INSTALACAO",
        propostaInstalacaoId: p.id,
        propostaInstalacaoNome: label,
        clienteId: p.clienteId || p.empresaId || null,
        competencia: competencia || null,
        competenciaMes: competenciaMes || null,
        quinzena: (isContratoQuinzenal(contratoTipo) && quinzena) ? quinzena : null,
        setorId: p.academiaId || p.setorId || null,
        setorNome: p.academiaNome || p.setorNome || "",
        clienteNome,
        academiaNome,
        label,
        subLabel,
      };
    });

    return [...prev, ...instalacoes, ...os];
  }, [backlogPreventivas, backlogOs, backlogInstalacoes, academiaId, isGlobalCliente, competencia, competenciaMes, quinzena, contratoTipo]);

  const leftItens = useMemo(() => {
    const rightIds = new Set((itensVisita || []).map((i) => i.id));
    return backlogItens.filter((i) => !rightIds.has(i.id));
  }, [backlogItens, itensVisita]);

  const moveRight = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;

    const setorKey = (item) => String(item?.setorId || "__SEM_SETOR__");
    const setorNome = (item) => String(item?.setorNome || "Sem academia");
    const tipoKey = (item) => normalizarTipoItem(item);

    const itensAtuais = Array.isArray(itensVisita) ? itensVisita : [];
    const baseSetor = itensAtuais.length > 0 ? setorKey(itensAtuais[0]) : null;
    const baseSetorNome = itensAtuais.length > 0 ? setorNome(itensAtuais[0]) : "";
    const baseTipo = itensAtuais.length > 0 ? tipoKey(itensAtuais[0]) : null;

    const setoresNovos = new Set(items.map((i) => setorKey(i)));
    if (setoresNovos.size > 1) {
      setErro("Não pode adicionar itens de academias diferentes na mesma visita.");
      return;
    }

    const tiposNovos = new Set(items.map((i) => tipoKey(i)));
    if (tiposNovos.size > 1) {
      setErro("Não pode misturar tipos diferentes (OS, Preventivas, Instalações) na mesma visita.");
      return;
    }

    const novoSetor = items[0] ? setorKey(items[0]) : null;
    const novoSetorNome = items[0] ? setorNome(items[0]) : "";
    if (baseSetor && novoSetor && baseSetor !== novoSetor) {
      setErro(`Esta visita já tem itens da academia \"${baseSetorNome}\". Remova-os para adicionar itens da academia \"${novoSetorNome}\".`);
      return;
    }

    const novoTipo = items[0] ? tipoKey(items[0]) : null;
    if (baseTipo && novoTipo && baseTipo !== novoTipo) {
      setErro(`Esta visita já é do tipo \"${baseTipo}\". Remova os itens atuais para adicionar \"${novoTipo}\".`);
      return;
    }

    setErro("");
    setItensVisita((prev) => {
      const map = new Map((prev || []).map((i) => [i.id, i]));
      items.forEach((i) => map.set(i.id, i));
      return Array.from(map.values());
    });
  };

  const moveLeft = (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const ids = new Set(items.map((i) => i.id));
    setItensVisita((prev) => (prev || []).filter((i) => !ids.has(i.id)));
  };

  const buscarContratoClienteAcademia = async (clienteIdRef, setorIdRef) => {
    if (!scope?.grupoId || !clienteIdRef) return null;

    const filtros = [
      where("grupoId", "==", scope.grupoId),
      where("clienteId", "==", clienteIdRef),
    ];

    if (setorIdRef) {
      filtros.push(where("academiasIds", "array-contains", setorIdRef));
    }

    const snap = await getDocs(query(collection(db, "financeiro_contratos"), ...filtros));
    const lista = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    if (!lista.length) return null;

    const aprovados = lista.filter((c) => String(c.status || "").toUpperCase() === "APROVADO");
    return aprovados[0] || lista[0];
  };

  const salvar = async () => {
    setErro("");
    setSucesso("");

    if (!scope?.grupoId) return setErro("Grupo não identificado para criar visita.");
    if (!Array.isArray(itensVisita) || itensVisita.length === 0) return setErro("Selecione ao menos 1 item.");

    const tipoVisita = obterTipoVisita(itensVisita);
    if (!tipoVisita) {
      return setErro("Não pode misturar tipos diferentes (OS, Preventivas, Instalações) na mesma visita.");
    }

    const competenciasItens = new Set(
      (itensVisita || [])
        .map((i) => String(i?.competencia || "").trim())
        .filter(Boolean)
    );
    if (competenciasItens.size > 1) {
      return setErro("Não pode misturar competências diferentes na mesma visita.");
    }
    const competenciaInferida = competenciasItens.size === 1 ? Array.from(competenciasItens)[0] : "";
    const competenciaFallback = (() => {
      const now = new Date();
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    })();
    const competenciaFinal = competencia || competenciaInferida || competenciaFallback;

    const clientesItens = new Set(
      (itensVisita || [])
        .map((i) => String(i?.clienteId || "").trim())
        .filter(Boolean)
    );
    if (clientesItens.size > 1) {
      return setErro("Não pode misturar clientes diferentes na mesma visita.");
    }

    const clienteInferido = clientesItens.size === 1 ? Array.from(clientesItens)[0] : "";
    const clienteIdFinal = (!isGlobalCliente && clienteId) ? clienteId : clienteInferido;
    if (!clienteIdFinal) {
      return setErro("Não foi possível identificar o cliente automaticamente pelos itens selecionados.");
    }

    const empresaIdFinal = empresaIdForOps || clienteIdFinal;
    if (!empresaIdFinal) {
      return setErro("Não foi possível identificar a empresa automaticamente pelos itens selecionados.");
    }

    // Não pode misturar academias diferentes na mesma visita.
    const setorIds = new Set((itensVisita || []).map((i) => i?.setorId).filter(Boolean).map((v) => String(v)));
    if (setorIds.size > 1) {
      return setErro("Não pode misturar academias diferentes na mesma visita. Selecione uma academia (filtro) ou remova itens de outras academias.");
    }
    const setorIdInferido = setorIds.size === 1 ? Array.from(setorIds)[0] : null;
    const setorIdFinal = academiaId || setorIdInferido || null;
    if (!setorIdFinal) {
      return setErro("Selecione uma academia para criar a visita (todos os itens precisam ser da mesma academia).");
    }

    const contratoInfo = await buscarContratoClienteAcademia(clienteIdFinal, setorIdFinal);
    const contratoTipoFinal = String(
      contratoInfo?.contratoTipo || (String(contratoTipo || "").toUpperCase() === "TODOS" ? "Avulso" : contratoTipo) || "Avulso"
    ).trim() || "Avulso";
    const contratoStatus = String(contratoInfo?.status || "").toUpperCase();
    const contratoAprovado = contratoStatus === "APROVADO";
    const contratoId = contratoInfo?.id || null;
    const visitasEmergenciais = Number(contratoInfo?.visitasEmergenciais ?? 0);
    const visitasUsadas = Number(contratoInfo?.visitasEmergenciaisUsadas ?? 0);
    const visitasRestantes = Math.max(visitasEmergenciais - visitasUsadas, 0);
    const temPreventivaNoPeriodo = backlogPreventivas.some(
      (p) => String(p.setorId || "") === String(setorIdFinal)
    );

    const isAvulso = isContratoAvulso(contratoTipoFinal) || !contratoAprovado;
    let liberadaAgendamento = false;
    let propostaStatus = "SEM_PROPOSTA";
    let usarCota = false;

    if (tipoVisita === "PREVENTIVA") {
      if (!contratoAprovado) {
        return setErro("Contrato precisa estar aprovado para criar visita de preventiva.");
      }
      liberadaAgendamento = true;
      propostaStatus = "APROVADA";
    }

    if (tipoVisita === "INSTALACAO") {
      liberadaAgendamento = true;
      propostaStatus = "APROVADA";
    }

    if (tipoVisita === "OS") {
      if (!isAvulso) {
        if (visitasRestantes > 0) {
          liberadaAgendamento = true;
          propostaStatus = "APROVADA";
          usarCota = !temPreventivaNoPeriodo;
        } else if (temPreventivaNoPeriodo) {
          liberadaAgendamento = true;
          propostaStatus = "APROVADA";
        } else {
          liberadaAgendamento = false;
          propostaStatus = "SEM_PROPOSTA";
        }
      }
    }

    setSalvando(true);
    try {
      let at = null;

      // Regra de negócio: OS criadas separadamente não devem ser mescladas automaticamente
      // no mesmo atendimento (mesmo cliente/competência/academia).
      // Para OS corretiva, sempre cria um novo atendimento.
      if (tipoVisita !== "OS") {
        const existentes = await listarAtendimentosPorClienteCompetencia(
          clienteIdFinal,
          competenciaFinal,
          { empresaId: empresaIdFinal }
        );
        // Filtrar por setorId/academiaId para não misturar atendimentos de academias diferentes
        at = (existentes && existentes.length > 0)
          ? existentes.find((e) => String(e.setorId || e.academiaId || "") === String(setorIdFinal))
          : null;
      }

      if (!at?.id) {
        at = await criarAtendimento(
          clienteIdFinal,
          contratoTipoFinal,
          competenciaFinal,
          undefined,
          {
            empresaId: empresaIdFinal,
            setorId: setorIdFinal || null,
            academiaId: setorIdFinal || null,
          }
        );
      }
      if (!at?.id) throw new Error("Nao foi possivel obter/criar o atendimento.");

      if (contratoId) {
        await updateDoc(doc(db, "atendimentos_preventiva", at.id), {
          contratoId,
          contratoStatus: contratoStatus || null,
          updatedAt: serverTimestamp(),
        });
      }

      const sess = await criarSessao(
        at.id,
        null,
        null,
        null,
        "PLANEJADA",
        null,
        { empresaId: empresaIdFinal }
      );
      if (!sess?.id) throw new Error("Nao foi possivel criar a sessao.");

      await updateDoc(doc(db, "atendimentos_sessoes", sess.id), {
        propostaStatus,
        liberadaAgendamento,
        propostaId: null,
        contratoId: contratoId || null,
        contratoStatus: contratoStatus || null,
        emergencialContrato: usarCota,
        emergencialOrigem: usarCota ? "CONTRATO_COTA" : null,
        emergencialConsumoPendente: usarCota,
        emergencialConsumido: false,
        updatedAt: serverTimestamp(),
      });

      await adicionarItensSessao(at.id, sess.id, clienteIdFinal, itensVisita, { empresaId: empresaIdFinal });

      if (tipoVisita === "INSTALACAO") {
        const propostasInstalacao = itensVisita
          .filter((i) => i?.propostaInstalacaoId)
          .map((i) => i.propostaInstalacaoId);

        await Promise.all(
          propostasInstalacao.map((pid) =>
            updateDoc(doc(db, "financeiro_propostas_instalacoes", pid), {
              sessaoId: sess.id,
              atendimentoId: at.id,
              updatedAt: serverTimestamp(),
            })
          )
        );
      }

      setSucesso("Visita criada com sucesso.");
      navigate(urlReturnTo || "/planejamento/atendimentos-visitas");
    } catch (e) {
      console.error(e);
      setErro(e?.message || "Erro ao criar visita.");
    } finally {
      setSalvando(false);
    }
  };

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Planejamento"
        abaBottom="home"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta((prev) => !prev)}
      >
        <div className="m-alert m-alert-erro">
          O módulo Planejamento está disponível apenas no desktop.
        </div>
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop tema={tema} titulo="Planejamento" subtitulo="Nova visita">
      <section className="pc-card nova-visita-card">
        <header className="nova-visita-header">
          <div>
            <h3>Nova visita</h3>
            <p>Selecione itens da mesma academia e do mesmo tipo de servico.</p>
          </div>
          <div className="nova-visita-actions">
            <button type="button" className="pc-btn-secundario" onClick={() => navigate(-1)}>
              Voltar
            </button>
            <button type="button" className="pc-btn-primario" onClick={salvar} disabled={salvando}>
              {salvando ? "Salvando..." : "Criar visita"}
            </button>
          </div>
        </header>

        {erro && <div className="pc-alert pc-alert-erro">{erro}</div>}
        {sucesso && <div className="pc-alert pc-alert-sucesso">{sucesso}</div>}

        <div className="nova-visita-form">
          <div className="nova-visita-panel">
            <div className="nova-visita-grid">
              <label>
                Buscar cliente
                <input
                  type="text"
                  value={clienteBusca}
                  onChange={(e) => setClienteBusca(e.target.value)}
                  placeholder="Digite para filtrar..."
                />
              </label>

              <label>
                Cliente
                <select
                  value={clienteId}
                  onChange={(e) => {
                    setClienteId(e.target.value);
                    setItensVisita([]);
                  }}
                >
                  <option value="">Todos (sem filtro)</option>
                  <option value={GLOBAL_CLIENTE_ID}>Global (todas OS nao agendadas)</option>
                  {clientesFiltrados.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nome || c.razaoSocial || "Cliente"}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Contrato
                <select value={contratoTipo} onChange={(e) => setContratoTipo(e.target.value)}>
                  <option value="TODOS">Todos (sem filtro)</option>
                  <option value="Mensal">Mensal</option>
                  <option value="Quinzenal">Quinzenal</option>
                  <option value="Trimestral">Trimestral</option>
                  <option value="Semestral">Semestral</option>
                  <option value="Avulso">Avulso</option>
                </select>
              </label>

              <label>
                Academia
                <select
                  value={academiaId}
                  onChange={(e) => setAcademiaId(e.target.value)}
                  disabled={!clienteId || isGlobalCliente}
                >
                  <option value="">Todas</option>
                  {academias.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.nome || a.codigo || a.id}
                    </option>
                  ))}
                </select>
              </label>

              <div className="nova-visita-competencia">
                <div className="nova-visita-competencia-row">
                  <label>
                    Competência (mês)
                    <input
                      type="month"
                      value={competenciaMes || ""}
                      onChange={(e) => setCompetenciaMes(e.target.value)}
                    />
                  </label>

                  {isContratoQuinzenal(contratoTipo) ? (
                    <label>
                      Quinzena
                      <select value={quinzena} onChange={(e) => setQuinzena(e.target.value)}>
                        <option value="Q1">Q1 (1–14)</option>
                        <option value="Q2">Q2 (15–fim)</option>
                      </select>
                    </label>
                  ) : null}
                </div>

                <div>
                  Competência gerada: <strong>{competencia || "-"}</strong>
                </div>

                <div className="mf-muted">
                  Regra: MENSAL inclui itens quinzenais + mensais; TRIMESTRAL inclui quinzenais + mensais + trimestrais;
                  SEMESTRAL inclui todos.
                </div>
              </div>

            </div>
          </div>

          {loadingBacklog && (
            <div className="pc-alert">Carregando instalações, OS e preventivas abertas...</div>
          )}

          <div className="nova-visita-panel nova-visita-transfer">
            <div className="nova-visita-transfer-header">
              <strong>Itens</strong>
              <span>Selecionados: {itensVisita.length}</span>
            </div>
            <TransferList
              titleLeft="Instalações + OS + Preventivas"
              titleRight="Itens da visita"
              selectedCountLabel="Itens"
              itemsLeft={leftItens}
              itemsRight={itensVisita}
              onMoveRight={moveRight}
              onMoveLeft={moveLeft}
              filterOptions={[]}
              getItemLabel={(item) => item.label}
              getItemSubLabel={(item) => item.subLabel}
              getItemGroup={() => ""}
              treeMode
              getTreeGroupLabel={(item) => `${item.clienteNome || "Sem cliente"} / ${item.academiaNome || "Sem academia"}`}
              pageSize={60}
            />
          </div>
        </div>
      </section>
    </LayoutDesktop>
  );
}
